
import { Component, Vue, Watch } from "vue-property-decorator";
import Pagination from "@/components/Pagination/index.vue";
import {queryUserSuppliers} from "@/api/supplier";
import {getOrderStatus, queryPoDelayDelivery, queryPoDeliveryTrack} from "@/api/purchase-order";
import Template from "@/views/system-management/template/index.vue";

interface DeliveryTrackingRow {
  id: string;
  code: string; // 采购订单号
  status: { name: string; desc: string }; // 采购订单状态
  orderDate: string; // 下单日期
  deliveryDate: string; // 约定交期
  remainDeliveryDays: number; // 剩余交货天数
  overdueDays: number; // 超时天数
  orderAmount: number; // 订单金额
  deliveryAmount: number; // 交货金额
  orderQty: number; // 下单数量
  deliveryQty: number; // 交货数量
  amountRate: number; // 金额交货率
  quantityRate: number; // 数量交货率
}

interface SupplierSummary {
  id: string;
  supplierCode: string;
  supplierAbbr: string;
  purchaser: string;
  overdueCount: number;
}

interface ListQueryModel {
  codeKeyword: string;
  states: string[];
  lastStates: string[];
  orderTimeOrOverdueDaysDesc: boolean;
}

@Component({
  name: "poDeliveryTracking",
  components: {
    Template,
    Pagination,
  },
})
export default class extends Vue {
  private supplierList: SupplierSummary[] = [];
  private supplierListLoading = false;
  public supplierPageNumber = 1;
  public supplierPageSize = 20;
  private supplierPageTotal = 0;
  public activeSupplierId = "";
  private activeSupplier: SupplierSummary = {
    id: "",
    supplierCode: "",
    supplierAbbr: "",
    purchaser: "",
    overdueCount: 0,
  };
  public supplierKeyword = "";
  private tableData: DeliveryTrackingRow[] = [];
  private tableDataLoading = false;
  private pageNumber = 1;
  private pageSize = 20;
  private pageTotal = 0;
  public listQuery: ListQueryModel = {
    codeKeyword: "",
    states: ['TO_BE_CONFIRMED_BY_PROCUREMENT', 'TO_BE_UPLOADED_WITH_RECEIPT', 'NOT_DELIVERED', 'PARTIALLY_DELIVERED'],
    lastStates: ['TO_BE_CONFIRMED_BY_PROCUREMENT', 'TO_BE_UPLOADED_WITH_RECEIPT', 'NOT_DELIVERED', 'PARTIALLY_DELIVERED'],
    orderTimeOrOverdueDaysDesc: true,
  };
  public statusFilterOptions = [
    {name: "TO_BE_CONFIRMED_BY_PROCUREMENT", desc: "待采购确认"},
    {name: "TO_BE_UPLOADED_WITH_RECEIPT", desc: "待上传回执"},
    {name: "NOT_DELIVERED", desc: "未交货"},
    {name: "PARTIALLY_DELIVERED", desc: "部分交货"},
    {name: "DELIVERY_COMPLETED", desc: "交货已完成"}
  ];

  get currentSupplierLabel(): string {
    return this.activeSupplier
        ? this.activeSupplier.supplierAbbr
            ? this.activeSupplier.supplierAbbr : "--"
        : "--";
  }

  get currentPurchaserLabel(): string {
    return this.activeSupplier
        ? this.activeSupplier.purchaser
            ? this.activeSupplier.purchaser : "--"
        : "--";
  }

  @Watch('activeSupplierId')
  private onActiveSupplierIdChange() {
    this.getDeliveryTrackingData();
  }

  async created() {
    await this.getSupplierList();
    // this.activeSupplierId = this.supplierList.length ? this.supplierList[0].id : "";
    // this.ensureActiveSupplierOnCurrentPage();
  }

  private async getSupplierList() {
    this.supplierListLoading = true;
    try {
      const params = {
        keyword: this.supplierKeyword,
        pageNumber: this.supplierPageNumber,
        pageSize: this.supplierPageSize
      }
      const supplierRes = await queryUserSuppliers(params) as any;
      this.supplierPageTotal = Number(supplierRes.totalPages);
      this.supplierList = supplierRes.content.map(item => {
        return {
          id: item.id,
          supplierCode: item.code,
          supplierAbbr: item.abbr,
          purchaser: item.purchaser
        };
      });
      const supplierIds = this.supplierList.map(item => item.id);
      const timeoutNumberRes = await queryPoDelayDelivery({supplierIds: supplierIds}) as any;
      this.supplierList.forEach(supplier => {
        const find = timeoutNumberRes.find(item => item.id === supplier.id);
        supplier.overdueCount = find.overduePoCount;
      });
    } finally {
      this.supplierListLoading = false;
    }
  }

  private async handleSupplierKeywordChange() {
    this.supplierPageNumber = 1;
    await this.getSupplierList();
    // this.ensureActiveSupplierOnCurrentPage();
  }

  private async getDeliveryTrackingData() {
    this.tableDataLoading = true;
    try {
      const params = {
        supplierId: this.activeSupplierId,
        ...this.listQuery,
        pageNumber: this.pageNumber,
        pageSize: this.pageSize
      }
      const res = await queryPoDeliveryTrack(params) as any;
      this.tableData = res.list.map(item => {
        return {
          amountRate: item.orderAmount !== 0 ? Math.floor((item.deliveryAmount / item.orderAmount) * 100) : 0,
          quantityRate: item.orderQty !== 0 ? Math.floor((item.deliveryQty / item.orderQty) * 100) : 0,
          ...item,
        }
      });
      this.pageTotal = Number(res.total);
    } finally {
      this.tableDataLoading = false;
    }
  }

  public handleStatusFilterChange(): void {
    if (this.listQuery.states.length === 0) {
      this.listQuery.states = this.listQuery.lastStates;
      return;
    }
    this.listQuery.lastStates = this.listQuery.states;
    this.pageNumber = 1;
    this.getDeliveryTrackingData();
  }

  public setSortType(type: boolean): void {
    this.listQuery.orderTimeOrOverdueDaysDesc = type;
    this.pageNumber = 1;
    this.getDeliveryTrackingData();
  }

  public handleSupplierChange(supplierId: string): void {
    this.activeSupplierId = supplierId;
    this.activeSupplier = this.supplierList.find((item) => item.id === this.activeSupplierId);
    this.pageNumber = 1;
  }

  private async handleSupplierPrevPage() {
    if (this.supplierPageNumber <= 1) {
      return;
    }
    this.supplierPageNumber -= 1;
    await this.getSupplierList();
    // this.ensureActiveSupplierOnCurrentPage();
  }

  private async handleSupplierNextPage() {
    if (this.supplierPageNumber >= this.supplierPageTotal) {
      return;
    }
    this.supplierPageNumber += 1;
    await this.getSupplierList();
    // this.ensureActiveSupplierOnCurrentPage();
  }

  public ensureActiveSupplierOnCurrentPage(): void {
    const currentPageList = this.supplierList;
    if (!currentPageList.length) {
      return;
    }
    const activeInCurrentPage = currentPageList.some(
      (item) => item.id === this.activeSupplierId
    );
    if (!activeInCurrentPage) {
      this.activeSupplierId = currentPageList[0].id;
      this.activeSupplier = currentPageList[0];
      this.pageNumber = 1;
    }
  }

  public statusTagType(status: string): string {
    if (status === "DELIVERY_COMPLETED") {
      return "success";
    }
    if (status === "PARTIALLY_DELIVERED") {
      return "warning";
    }
    if (status === "NOT_DELIVERED") {
      return "info";
    }
    return "";
  }

  public progressColor(value: number): string {
    if (value >= 100) {
      return "#67c23a";
    }
    if (value >= 60) {
      return "#e6a23c";
    }
    return "#f56c6c";
  }

  public getRemainingDays(row: DeliveryTrackingRow): number | null {
    if (!row.deliveryDate) {
      return null;
    }
    if (row.status.name === "DELIVERY_COMPLETED") {
      return null;
    }
    const days = row.remainDeliveryDays;
    if (days === null) {
      return null;
    }
    return days > 0 ? days : 0;
  }

  public isOverdue(row: DeliveryTrackingRow) {
    const days = this.getOverdueDaysText(row);
    return days > 0;
  }

  private getDeliveryDate(row: DeliveryTrackingRow) {
    if (row.status.name !== "DELIVERY_COMPLETED") {
      return row.deliveryDate? row.deliveryDate : "--";
    } else {
      if (row.deliveryDate) {
        return row.deliveryDate;
      } else {
        return "error";
      }
    }
  }

  public getRemainingDaysText(row: DeliveryTrackingRow) {
    if (row.status.name !== "DELIVERY_COMPLETED") {
      if (row.deliveryDate) {
        if (row.remainDeliveryDays != null) {
          return row.remainDeliveryDays >= 0 ? row.remainDeliveryDays : "";
        } else {
          return "error";
        }
      } else {
        return "--";
      }
    } else {
      return "";
    }
  }

  public getOverdueDaysText(row: DeliveryTrackingRow) {
    if (row.status.name !== "DELIVERY_COMPLETED") {
      if (row.deliveryDate) {
        if (row.overdueDays != null) {
          return row.overdueDays >= 0 ? row.overdueDays : "";
        } else {
          return "error";
        }
      } else {
        return "--";
      }
    } else {
      if (row.deliveryDate) {
        if (row.overdueDays != null) {
          return row.overdueDays > 0 ? row.overdueDays : "";
        } else {
          return "noDate";
        }
      } else {
        return "error";
      }
    }
  }

  public remainingDayClass(row: DeliveryTrackingRow): string {
    const days = this.getRemainingDaysText(row);
    if (days >= 0 && days <= 10) {
      return "text-warning";
    }
    return "";
  }

  public overdueDayClass(row: DeliveryTrackingRow): string {
    const overdueDays = this.getOverdueDaysText(row);
    if (row.status.name !== "DELIVERY_COMPLETED" && overdueDays > 0) {
      return "text-danger";
    }
    if (row.status.name === "DELIVERY_COMPLETED" && overdueDays > 0) {
      return "text-muted";
    }
    return "";
  }

  public isNearlyDue(row: DeliveryTrackingRow): boolean {
    const days = this.getRemainingDaysText(row);
    return days >= 0 && days <= 10;
  }

  public getRowClassName({ row }: { row: DeliveryTrackingRow }): string {
    if (this.isOverdue(row)) {
      return "row-danger";
    }
    if (this.isNearlyDue(row)) {
      return "row-warning";
    }
    return "";
  }
}
